import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Sparkles } from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

const clampN = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const wordCount = (s) => (s.trim().match(/\S+/g) || []).length;

/**
 * Detects a deliberate text selection inside a reading container and exposes it
 * (term + surrounding passage + rect) so a contextual "Explain" action can be
 * offered. Ignores collapsed/empty/huge selections so accidental clicks never
 * trigger an explanation.
 */
export function useSelectionExplain(containerRef) {
    const [sel, setSel] = useState(null);
    const clear = useCallback(() => setSel(null), []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const handle = () => {
            setTimeout(() => {
                const s = window.getSelection();
                if (!s || s.isCollapsed || s.rangeCount === 0) return setSel(null);
                const text = s.toString().replace(/\s+/g, " ").trim();
                if (!text || !/\p{L}/u.test(text)) return setSel(null);
                const wc = wordCount(text);
                if (wc < 1 || wc > 12 || text.length > 140) return setSel(null);
                const range = s.getRangeAt(0);
                if (!el.contains(range.commonAncestorContainer)) return setSel(null);
                const r = range.getBoundingClientRect();
                if (!r || (r.width === 0 && r.height === 0)) return setSel(null);
                let node = range.startContainer;
                while (node && node.nodeType !== 1) node = node.parentNode;
                const p = node && node.closest ? node.closest("p") : null;
                const tokEl = node && node.closest ? node.closest("[data-tok]") : null;
                const dataTok = tokEl ? parseInt(tokEl.getAttribute("data-tok"), 10) : null;
                const context = ((p ? p.textContent : text) || "").replace(/\s+/g, " ").trim().slice(0, 600);
                setSel({ term: text, context, dataTok: Number.isFinite(dataTok) ? dataTok : null, rect: { top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height } });
            }, 10);
        };
        const onScroll = () => setSel(null);
        el.addEventListener("mouseup", handle);
        el.addEventListener("touchend", handle);
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            el.removeEventListener("mouseup", handle);
            el.removeEventListener("touchend", handle);
            el.removeEventListener("scroll", onScroll);
        };
    }, [containerRef]);

    return { sel, clear };
}

export const SelectionExplainLayer = ({ containerRef, onExplain, ratio = 1 }) => {
    const { t } = useTranslation();
    const { sel, clear } = useSelectionExplain(containerRef);
    if (!sel) return null;
    const { rect } = sel;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
    const top = Math.max(8, rect.top - 42);
    const left = Math.round(clampN(rect.left + rect.width / 2 - 60, 8, vw - 128));
    const wordIndex = sel.dataTok != null ? Math.round(sel.dataTok * ratio) : null;
    return createPortal(
        <button
            data-testid="selection-explain-button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
                onExplain?.(sel.term, sel.context, rect, wordIndex);
                clear();
                const s = window.getSelection();
                if (s) s.removeAllRanges();
            }}
            style={{ position: "fixed", top, left, zIndex: 65 }}
            className="inline-flex animate-fade-in items-center gap-1.5 rounded-full border border-[hsl(var(--primary))]/40 bg-surface-1 px-3 py-1.5 text-xs font-medium text-[hsl(var(--primary))] shadow-elev-2 transition-transform hover:scale-105"
        >
            <Sparkles className="h-3.5 w-3.5" /> {t("explain.title")}
        </button>,
        document.body
    );
};
